import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VerifiedIcon from '@mui/icons-material/Verified';
import salesService from '../../../../services/salesService';
import { PackageOrder, TrendyolOrder } from '../types';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../../common/guide/GuideButton';

// ============================================================================
// TYPES
// ============================================================================

interface VerificationItem {
  orderNumber: string;
  customerName: string;
  totalAmount: number;
  date: string;
  platform: string;
  invoiceNumber: string;
  hasInvoice: boolean;
  hasReceipt: boolean;
  hasPayment: boolean;
  isPaid: boolean;
  isCancelledOrder: boolean;
  status: 'ok' | 'warning' | 'error';
  message?: string;
}

interface PlatformGroup {
  label: string;
  key: string;
  items: VerificationItem[];
  okCount: number;
  warningCount: number;
  errorCount: number;
}

interface PaymentVerificationModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  branchId?: string | null;
  orders: PackageOrder[];
  getirOrders: any[];
  migrosOrders: any[];
  yemeksepetiOrders: any[];
  trendyolOrders: TrendyolOrder[];
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'TRY' }).format(amount);
};

const formatDate = (dateStr: string): string => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    return d.toLocaleString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return dateStr;
  }
};

/**
 * Fiş eşleştirmesinde tek adayı seçer.
 *
 * Çoklu mağazada aynı ShortCode iki markada birden görülebiliyor; bu durumda
 * ilk adayı almak bir markanın ödemesini diğerine yazar. Birden fazla aday
 * varsa tutarla ayrıştırılır, o da tek sonuç vermezse eşleşme yapılmaz —
 * eksik eşleşme, yanlış eşleşmeden iyidir.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const pickUniqueReceipt = <T extends { TotalAmount: number }>(
  candidates: T[],
  expectedAmount: number,
  label: string,
): T | undefined => {
  if (candidates.length === 0) return undefined;
  if (candidates.length === 1) return candidates[0];

  const byAmount = candidates.filter(r => Math.abs(r.TotalAmount - expectedAmount) < 0.02);
  if (byAmount.length === 1) return byAmount[0];

  console.warn('[KONTROL_MODAL] Fiş eşleşmesi belirsiz, atlandı:', {
    siparis: label,
    adaySayisi: candidates.length,
    tutarEsleseniSayisi: byAmount.length,
    beklenenTutar: expectedAmount,
  });
  return undefined;
};

// ============================================================================
// COMPONENT
// ============================================================================

const PaymentVerificationModal: React.FC<PaymentVerificationModalProps> = ({
  open,
  onClose,
  companyId,
  branchId,
  orders,
  getirOrders,
  migrosOrders,
  yemeksepetiOrders,
  trendyolOrders,
}) => {
  const { t } = useTranslation('salesSystem');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [platformGroups, setPlatformGroups] = useState<PlatformGroup[]>([]);
  const [expandedPanels, setExpandedPanels] = useState<Set<string>>(new Set());

  const ordersRef = useRef(orders);
  const getirOrdersRef = useRef(getirOrders);
  const migrosOrdersRef = useRef(migrosOrders);
  const yemeksepetiOrdersRef = useRef(yemeksepetiOrders);
  const trendyolOrdersRef = useRef(trendyolOrders);

  ordersRef.current = orders;
  getirOrdersRef.current = getirOrders;
  migrosOrdersRef.current = migrosOrders;
  yemeksepetiOrdersRef.current = yemeksepetiOrders;
  trendyolOrdersRef.current = trendyolOrders;

  const runVerification = useCallback(async (forceRun?: boolean) => {
    if (!companyId) return;
    setLoading(true);
    setError(null);

    const currentOrders = ordersRef.current;
    const currentGetirOrders = getirOrdersRef.current;
    const currentMigrosOrders = migrosOrdersRef.current;
    const currentYemeksepetiOrders = yemeksepetiOrdersRef.current;
    const currentTrendyolOrders = trendyolOrdersRef.current;

    console.log('[KONTROL_MODAL] Doğrulama başlatılıyor...', {
      telefon: currentOrders.length,
      getir: currentGetirOrders.length,
      migros: currentMigrosOrders.length,
      yemeksepeti: currentYemeksepetiOrders.length,
      trendyol: currentTrendyolOrders.length,
    });

    try {
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 5, 0, 0);
      if (now.getHours() < 5) todayStart.setDate(todayStart.getDate() - 1);
      const todayEnd = new Date(todayStart);
      todayEnd.setDate(todayEnd.getDate() + 1);

      const startDate = todayStart.toISOString();
      const endDate = todayEnd.toISOString();

      const receipts = await salesService.getQuickSaleReceiptsByDateRange(startDate, endDate, branchId);

      console.log('[KONTROL_MODAL] Fişler:', receipts.length);

      const receiptInvoiceNumbers = new Set(receipts.map(r => r.InvoiceNumber).filter(Boolean));
      const receiptPaymentMap = new Map<string, boolean>();
      const invoiceNumberSet = new Set<string>();
      const invoiceIdSet = new Set<string>();
      const invoiceIdToNumber = new Map<string, string>();
      for (const r of receipts) {
        if (r.InvoiceNumber) {
          receiptPaymentMap.set(r.InvoiceNumber, (r.Payments?.length ?? 0) > 0);
          invoiceNumberSet.add(r.InvoiceNumber);
        }
        if (r.InvoiceId) {
          invoiceIdSet.add(r.InvoiceId);
          if (r.InvoiceNumber) invoiceIdToNumber.set(r.InvoiceId, r.InvoiceNumber);
        }
      }
      console.log('[KONTROL_MODAL] Fiş verisinden fatura sayısı:', invoiceNumberSet.size);

      const verify = (
        orderNumber: string,
        customerName: string,
        totalAmount: number,
        date: string,
        platform: string,
        invoiceNumber: string,
        invoiceId: string,
        isPaid: boolean,
        isCancelled: boolean,
      ): VerificationItem => {
        if (!invoiceNumber && invoiceId && invoiceIdToNumber.has(invoiceId)) {
          invoiceNumber = invoiceIdToNumber.get(invoiceId) || '';
        }

        const hasInvoice = !!(
          (invoiceNumber && invoiceNumberSet.has(invoiceNumber)) ||
          (invoiceId && invoiceIdSet.has(invoiceId))
        );
        const hasReceipt = !!(invoiceNumber && receiptInvoiceNumbers.has(invoiceNumber));
        const hasPayment = !!(invoiceNumber && receiptPaymentMap.get(invoiceNumber));

        let status: 'ok' | 'warning' | 'error' = 'ok';
        let message: string | undefined;

        if (isCancelled) {
          status = 'ok';
          message = t('orderManagement.paymentVerification.status.cancelled');
        } else if (isPaid && hasInvoice && hasReceipt && hasPayment) {
          status = 'ok';
        } else if (isPaid && !hasInvoice && !hasReceipt) {
          status = 'error';
          message = t('orderManagement.paymentVerification.messages.paidNoInvoiceNoReceipt');
        } else if (isPaid && hasInvoice && !hasReceipt) {
          status = 'warning';
          message = t('orderManagement.paymentVerification.messages.invoiceNoReceipt');
        } else if (isPaid && !hasInvoice && hasReceipt) {
          status = 'warning';
          message = t('orderManagement.paymentVerification.messages.receiptNoInvoice');
        } else if (isPaid && hasReceipt && !hasPayment) {
          status = 'warning';
          message = t('orderManagement.paymentVerification.messages.receiptNoPayment');
        } else if (!isPaid && !isCancelled) {
          if (hasInvoice || hasReceipt) {
            status = 'warning';
            message = t('orderManagement.paymentVerification.messages.unpaidButRecorded');
          } else {
            status = 'warning';
            message = t('orderManagement.paymentVerification.messages.paymentPending');
          }
        }

        return {
          orderNumber, customerName, totalAmount, date, platform,
          invoiceNumber, hasInvoice, hasReceipt, hasPayment, isPaid, isCancelledOrder: isCancelled, status, message,
        };
      };

      const groups: PlatformGroup[] = [];

      const excludedSources = ['kiosk', 'qr', 'qrmenu', 'ecommerce'];
      const phoneOrders = currentOrders.filter(o => {
        const src = (o.source || '').toLowerCase();
        return !excludedSources.includes(src);
      });
      if (phoneOrders.length > 0) {
        const items = phoneOrders.map(o => {
          let invNum = o.invoiceNumber || '';
          let invId = o.invoiceId || '';

          if (!invNum && !invId) {
            const shortNo = o.shortOrderNo || '';
            const extNo = o.externalOrderNo || '';
            const refNo = o.referenceNumber || '';
            let receipt = pickUniqueReceipt(
              receipts.filter(r =>
                (refNo && r.ReferenceNumber === refNo) ||
                (invNum && r.InvoiceNumber === invNum) ||
                (shortNo && (r.ReferenceNumber === shortNo || r.ShortCode === shortNo)) ||
                (extNo && (r.ReferenceNumber === extNo || r.ShortCode === extNo))
              ),
              o.totalAmount,
              `${o.source}/${shortNo || extNo || refNo}`,
            );
            if (!receipt && (shortNo || extNo || refNo)) {
              receipt = pickUniqueReceipt(
                receipts.filter(r =>
                  (refNo && r.ReferenceNumber?.includes(refNo)) ||
                  (shortNo && (r.ReferenceNumber?.includes(shortNo) || r.ShortCode?.includes(shortNo))) ||
                  (extNo && (r.ReferenceNumber?.includes(extNo) || r.ShortCode?.includes(extNo)))
                ),
                o.totalAmount,
                `${o.source}/${shortNo || extNo || refNo} (kismi eslesme)`,
              );
            }
            if (receipt) {
              invNum = receipt.InvoiceNumber || '';
              invId = receipt.InvoiceId || '';
            }
          }

          const isCancelled = o.status === 'cancelled';
          return verify(o.shortOrderNo, o.customerName, o.totalAmount, o.createdAt || o.orderDate, t('orderManagement.paymentVerification.groups.phoneOrders'), invNum, invId, o.isPaid, isCancelled);
        });
        groups.push(buildGroup(t('orderManagement.paymentVerification.groups.phoneOrders'), 'phone', items));
      }

      // Getir
      if (currentGetirOrders.length > 0) {
        const items = currentGetirOrders.map((o: any) => {
          const invNum = o.invoiceNumber ?? o.InvoiceNumber ?? '';
          const invId = o.invoiceId ?? o.InvoiceId ?? '';
          const isPaid = (o.paymentStatus ?? o.PaymentStatus ?? '').toUpperCase() === 'PAID';
          const statusVal = o.status ?? o.Status ?? o.getirStatus ?? '';
          const isCancelled = [1500, '1500', 1600, '1600', 'CANCELLED'].includes(statusVal);
          const total = o.totalDiscountedPrice ?? o.totalPrice ?? 0;
          const name = o.clientName ?? o.ClientName ?? 'Getir';
          const date = o.createdAt ?? o.CreatedAt ?? o.receivedAt ?? '';
          const orderNum = o.confirmationId ?? o.ConfirmationId ?? String(o.id ?? '');
          return verify(orderNum, name, total, date, 'Getir', invNum, invId, isPaid, isCancelled);
        });
        groups.push(buildGroup('Getir', 'getir', items));
      }

      // Migros
      if (currentMigrosOrders.length > 0) {
        const items = currentMigrosOrders.map((o: any) => {
          const invNum = o.invoiceNumber ?? o.InvoiceNumber ?? '';
          const invId = o.invoiceId ?? o.InvoiceId ?? '';
          const isPaid = (o.paymentStatus ?? o.PaymentStatus ?? '').toUpperCase() === 'PAID';
          const statusRaw = (o.orderStatus ?? o.OrderStatus ?? '').toUpperCase();
          const isCancelled = statusRaw.includes('CANCEL') || statusRaw.includes('REJECT');
          const total = (o.discountedPrice ?? o.totalPrice ?? 0) / 100;
          const name = o.customerFullName ?? o.CustomerFullName ?? 'Migros';
          const date = o.createdAt ?? o.CreatedAt ?? '';
          const orderNum = String(o.migrosOrderId ?? o.id ?? '');
          return verify(orderNum, name, total, date, 'Migros', invNum, invId, isPaid, isCancelled);
        });
        groups.push(buildGroup('Migros', 'migros', items));
      }

      // Yemeksepeti
      if (currentYemeksepetiOrders.length > 0) {
        const items = currentYemeksepetiOrders.map((o: any) => {
          const invNum = o.invoiceNumber ?? o.InvoiceNumber ?? '';
          const invId = o.invoiceId ?? o.InvoiceId ?? '';
          const isPaid = (o.paymentStatus ?? o.PaymentStatus ?? '').toUpperCase() === 'PAID';
          const statusRaw = (o.status ?? o.Status ?? '').toUpperCase();
          const isCancelled = statusRaw.includes('CANCEL') || statusRaw.includes('REJECT');
          const total = o.grandTotal ?? o.GrandTotal ?? 0;
          const name = o.customerFullName ?? (`${o.customerFirstName ?? ''} ${o.customerLastName ?? ''}`.trim() || 'Yemeksepeti');
          const date = o.createdAt ?? o.CreatedAt ?? o.receivedAt ?? '';
          const orderNum = o.shortCode ?? o.ShortCode ?? String(o.id ?? '');
          return verify(orderNum, name, total, date, 'Yemeksepeti', invNum, invId, isPaid, isCancelled);
        });
        groups.push(buildGroup('Yemeksepeti', 'yemeksepeti', items));
      }

      // Trendyol
      if (currentTrendyolOrders.length > 0) {
        const trdReceipts = receipts.filter(r => r.ReferenceNumber?.startsWith('TRD-'));
        const usedTrdReceipts = new Set<number>();
        const items = currentTrendyolOrders.map(o => {
          const isPaid = !!o.gtsPaid;
          const statusRaw = (o.packageStatus || '').toUpperCase();
          const isCancelled = statusRaw.includes('CANCEL') || statusRaw === 'UNSUPPLIED';
          let receipt = pickUniqueReceipt(
            receipts.filter(r =>
              r.ReferenceNumber?.includes(o.orderNumber || '') || r.ShortCode?.includes(o.orderNumber || '')
            ),
            o.totalPrice,
            `trendyol/${o.orderNumber || o.orderCode}`,
          );
          if (!receipt) {
            receipt = trdReceipts.find(r =>
              !usedTrdReceipts.has(r.Id) && Math.abs(r.TotalAmount - o.totalPrice) < 0.02
            );
          }
          if (receipt) usedTrdReceipts.add(receipt.Id);
          const invNum = receipt?.InvoiceNumber || '';
          const invId = receipt?.InvoiceId || '';
          const name = o.customer ? `${o.customer.firstName || ''} ${o.customer.lastName || ''}`.trim() : 'Uber Eats';
          const date = o.packageCreationDate ? new Date(o.packageCreationDate).toISOString() : '';
          return verify(o.orderNumber || o.orderCode, name, o.totalPrice, date, 'Uber Eats', invNum, invId, isPaid, isCancelled);
        });
        groups.push(buildGroup('Uber Eats', 'trendyol', items));
      }

      setPlatformGroups(groups);

      const panelsWithIssues = new Set<string>();
      for (const g of groups) {
        if (g.warningCount > 0 || g.errorCount > 0) panelsWithIssues.add(g.key);
      }
      setExpandedPanels(panelsWithIssues);

    } catch (err: any) {
      console.error('[KONTROL] Doğrulama hatası:', err);
      setError(err.message || t('orderManagement.paymentVerification.errors.generic'));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, branchId]);

  const hasOpenedRef = React.useRef(false);
  useEffect(() => {
    if (open && !hasOpenedRef.current) {
      hasOpenedRef.current = true;
      runVerification();
    }
    if (!open) {
      hasOpenedRef.current = false;
    }
  }, [open, runVerification]);

  const summary = useMemo(() => {
    let total = 0, ok = 0, warning = 0, error = 0;
    for (const g of platformGroups) {
      total += g.items.length;
      ok += g.okCount;
      warning += g.warningCount;
      error += g.errorCount;
    }
    return { total, ok, warning, error };
  }, [platformGroups]);

  const handleAccordionChange = (key: string) => {
    setExpandedPanels(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { maxHeight: '90vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VerifiedIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">{t('orderManagement.paymentVerification.reportTitle')}</Typography>
          <GuideButton guideKey="sales.paymentVerification" size="small" tabIndex={-1} />
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
            <CircularProgress size={48} />
            <Typography sx={{ mt: 2 }} color="text.secondary">{t('orderManagement.paymentVerification.loading')}</Typography>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && !error && (
          <>
            {/* Summary Cards */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <SummaryCard label={t('orderManagement.paymentVerification.summary.totalOrders')} count={summary.total} color="#1976d2" />
              <SummaryCard label={t('orderManagement.paymentVerification.summary.ok')} count={summary.ok} color="#2e7d32" />
              <SummaryCard label={t('orderManagement.paymentVerification.summary.warning')} count={summary.warning} color="#ed6c02" />
              <SummaryCard label={t('orderManagement.paymentVerification.summary.error')} count={summary.error} color="#d32f2f" />
            </Box>

            {summary.total === 0 && (
              <Alert severity="info">{t('orderManagement.paymentVerification.empty')}</Alert>
            )}

            {/* Platform Groups */}
            {platformGroups.map(group => (
              <Accordion
                key={group.key}
                expanded={expandedPanels.has(group.key)}
                onChange={() => handleAccordionChange(group.key)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Typography fontWeight="bold">{group.label}</Typography>
                    <Chip label={t('orderManagement.paymentVerification.orderCount', { count: group.items.length })} size="small" variant="outlined" />
                    {group.okCount > 0 && (
                      <Badge badgeContent={group.okCount} color="success" sx={{ '& .MuiBadge-badge': { fontSize: 10 } }}>
                        <CheckCircleIcon fontSize="small" color="success" />
                      </Badge>
                    )}
                    {group.warningCount > 0 && (
                      <Badge badgeContent={group.warningCount} color="warning" sx={{ '& .MuiBadge-badge': { fontSize: 10 } }}>
                        <WarningAmberIcon fontSize="small" color="warning" />
                      </Badge>
                    )}
                    {group.errorCount > 0 && (
                      <Badge badgeContent={group.errorCount} color="error" sx={{ '& .MuiBadge-badge': { fontSize: 10 } }}>
                        <CancelIcon fontSize="small" color="error" />
                      </Badge>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <VerificationTable items={group.items} />
                </AccordionDetails>
              </Accordion>
            ))}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={() => runVerification()} disabled={loading} variant="outlined" size="small">
          {t('orderManagement.paymentVerification.actions.recheck')}
        </Button>
        <Button onClick={onClose} variant="contained" size="small">
          {t('orderManagement.paymentVerification.actions.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const SummaryCard: React.FC<{ label: string; count: number; color: string }> = ({ label, count, color }) => (
  <Paper
    elevation={0}
    sx={{
      flex: '1 1 140px', p: 1.5, textAlign: 'center',
      border: '1px solid', borderColor: 'divider', borderRadius: 2,
    }}
  >
    <Typography variant="h5" fontWeight="bold" sx={{ color }}>{count}</Typography>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Paper>
);

const VerificationTable: React.FC<{ items: VerificationItem[] }> = ({ items }) => {
  const { t } = useTranslation('salesSystem');
  const sorted = useMemo(() =>
    [...items].sort((a, b) => {
      const pri = { error: 0, warning: 1, ok: 2 };
      return (pri[a.status] ?? 2) - (pri[b.status] ?? 2);
    }),
    [items]
  );

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 'bold', fontSize: 11 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 'bold', fontSize: 11 }}>{t('orderManagement.paymentVerification.table.orderNumber')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold', fontSize: 11 }}>{t('orderManagement.paymentVerification.table.customer')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold', fontSize: 11 }} align="right">{t('orderManagement.paymentVerification.table.amount')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold', fontSize: 11 }}>{t('orderManagement.paymentVerification.table.invoice')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold', fontSize: 11 }}>{t('orderManagement.paymentVerification.table.receipt')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold', fontSize: 11 }}>{t('orderManagement.paymentVerification.table.payment')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold', fontSize: 11 }}>{t('orderManagement.paymentVerification.table.status')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((item, idx) => (
            <TableRow
              key={`${item.orderNumber}-${idx}`}
              sx={{
                bgcolor: item.status === 'error' ? '#ffebee' : item.status === 'warning' ? '#fff8e1' : undefined,
                '&:hover': { bgcolor: '#f0f0f0' },
              }}
            >
              <TableCell sx={{ fontSize: 11 }}>{idx + 1}</TableCell>
              <TableCell sx={{ fontSize: 11, fontWeight: 500 }}>{item.orderNumber || '-'}</TableCell>
              <TableCell sx={{ fontSize: 11 }}>{item.customerName}</TableCell>
              <TableCell sx={{ fontSize: 11, fontWeight: 500 }} align="right">{formatCurrency(item.totalAmount)}</TableCell>
              <TableCell>
                {item.hasInvoice
                  ? <CheckCircleIcon fontSize="small" sx={{ color: '#2e7d32', fontSize: 16 }} />
                  : <CancelIcon fontSize="small" sx={{ color: '#d32f2f', fontSize: 16 }} />}
              </TableCell>
              <TableCell>
                {item.hasReceipt
                  ? <CheckCircleIcon fontSize="small" sx={{ color: '#2e7d32', fontSize: 16 }} />
                  : <CancelIcon fontSize="small" sx={{ color: '#d32f2f', fontSize: 16 }} />}
              </TableCell>
              <TableCell>
                {item.hasPayment
                  ? <CheckCircleIcon fontSize="small" sx={{ color: '#2e7d32', fontSize: 16 }} />
                  : <CancelIcon fontSize="small" sx={{ color: '#d32f2f', fontSize: 16 }} />}
              </TableCell>
              <TableCell>
                {item.status === 'ok' && !item.isCancelledOrder && (
                  <Chip label={t('orderManagement.paymentVerification.summary.ok')} size="small" color="success" variant="outlined" sx={{ fontSize: 10 }} />
                )}
                {item.status === 'ok' && item.isCancelledOrder && (
                  <Chip label={t('orderManagement.paymentVerification.status.cancelled')} size="small" sx={{ fontSize: 10, bgcolor: '#e0e0e0' }} />
                )}
                {item.status === 'warning' && (
                  <Chip label={item.message || t('orderManagement.paymentVerification.summary.warning')} size="small" color="warning" variant="outlined" sx={{ fontSize: 10 }} />
                )}
                {item.status === 'error' && (
                  <Chip label={item.message || t('orderManagement.paymentVerification.summary.error')} size="small" color="error" variant="outlined" sx={{ fontSize: 10 }} />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

function buildGroup(label: string, key: string, items: VerificationItem[]): PlatformGroup {
  return {
    label,
    key,
    items,
    okCount: items.filter(i => i.status === 'ok').length,
    warningCount: items.filter(i => i.status === 'warning').length,
    errorCount: items.filter(i => i.status === 'error').length,
  };
}

export default PaymentVerificationModal;
